import React, { FC } from 'react';
import i18n from 'i18n-js';

import { TimelineBlockTitle } from './TimelineBlockTitle';
import { ColorFunction } from '@styles/colors/types';
import { TIMELINE_UNDERLINE_TEXT_CONFIG } from '@components/machines/components/timeline/constants';
import { LegendRules } from '@components/machines/components/timeline/calculations';
import { Events, TimelineDataWithLoadingState } from '@interfaces/timeline';
import { MomentInput } from 'moment';
import TimelineShimmer from './TimelineShimmer';
import { ProductionBatch } from '@interfaces/search';
import { useManageTimelineBatches } from '@components/machines/components/timeline/hooks/useManageTimelineBatches';
import { getTimelineElementBorderRadius } from '@components/machines/components/timeline/helpers';

export interface TimelineBatchesProps {
  C: ColorFunction;
  blockWidth: number;
  batchesData: TimelineDataWithLoadingState<ProductionBatch[]>;
  rules: LegendRules;
  startTime: MomentInput;
  events: Events;
}

const TimelineBatches: FC<TimelineBatchesProps> = ({ C, blockWidth, batchesData, rules, startTime, events }) => {
  const { batchElements, unknownPercentage, fontStylesWeb, timelineBg, timelineBorderRadius, timelineLineHeight } = useManageTimelineBatches({
    batchesData,
    events,
    rules,
    startTime,
  });

  return (
    <div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <TimelineBlockTitle C={C}>{i18n.t('Batches')}</TimelineBlockTitle>
        {unknownPercentage > 0 && (
          <p style={{ ...fontStylesWeb, margin: 0, color: C('Label/Light/Tertiary') }}>
            {unknownPercentage}% {i18n.t('Not Linked')}
          </p>
        )}
      </div>

      <div
        style={{
          width: blockWidth,
          height: timelineLineHeight + TIMELINE_UNDERLINE_TEXT_CONFIG.height * 2,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {batchesData.loaded && events.loaded ? (
          <>
            <div
              style={{
                borderRadius: timelineBorderRadius,
                backgroundColor: C(timelineBg),
                height: timelineLineHeight,
                display: 'flex',
                flexDirection: 'row',
                position: 'relative',
                overflow: 'hidden',
              }}
            />
            <div style={{ position: 'relative', height: TIMELINE_UNDERLINE_TEXT_CONFIG.height * 2, overflow: 'hidden' }} />
            {batchElements?.map(({ b, elementLeft, elementWidth, originalWidth, shouldDisplayUnknown, batchStatusText }, index) => {
              // Use originalWidth for text display check to avoid showing text on tiny elements
              const shouldDisplayText = originalWidth > 3;

              const borderRadiusStyles = getTimelineElementBorderRadius({
                elementLeft,
                elementWidth,
                blockWidth,
                borderRadius: timelineBorderRadius,
              });

              return (
                <React.Fragment key={`${b?.startedOn}-${index}`}>
                  <div
                    style={{
                      ...borderRadiusStyles,
                      left: elementLeft,
                      width: elementWidth,
                      height: timelineLineHeight,
                      position: 'absolute',
                      top: 0,
                      backgroundColor:
                        b?.productionBatchId == null
                          ? 'transparent'
                          : C(!shouldDisplayUnknown ? 'System/Light/Cyan' : 'Background/Light/Production Information Missing'),
                    }}
                  />
                  {shouldDisplayText && (
                    <div
                      style={{
                        left: elementLeft,
                        width: originalWidth,
                        position: 'absolute',
                        top: timelineLineHeight,
                        zIndex: index,
                        overflow: 'hidden',
                      }}
                    >
                      <p
                        style={{
                          ...fontStylesWeb,
                          color: C(TIMELINE_UNDERLINE_TEXT_CONFIG.color),
                          margin: 0,
                          height: TIMELINE_UNDERLINE_TEXT_CONFIG.height,
                          whiteSpace: 'nowrap',
                          display: 'flex',
                          alignItems: 'center',
                          overflow: 'hidden',
                          width: '100%',
                        }}
                      >
                        {b?.batchNumber}
                      </p>
                      <div style={{ height: TIMELINE_UNDERLINE_TEXT_CONFIG.height, display: 'flex', alignItems: 'center' }}>
                        <p
                          style={{
                            margin: 0,
                            ...fontStylesWeb,
                            color: C(TIMELINE_UNDERLINE_TEXT_CONFIG.color),
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            width: '100%',
                          }}
                        >
                          {batchStatusText}
                        </p>
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </>
        ) : (
          <TimelineShimmer style={{ height: timelineLineHeight, borderRadius: timelineBorderRadius }} />
        )}
      </div>
    </div>
  );
};

export { TimelineBatches };
